import type { Arc } from './arc';
import { IN_FLIGHT, innerArcNames, isLive } from './arc';
import type { BoardRow } from './board';

/**
 * The board's flat rows, grouped for the layouts that use horizontal space.
 *
 * `buildBoard` emits one ordered list per status, with depth marking what sits
 * inside a storyline. That is exactly right for a column of rows and useless
 * for a wall, which needs to know where one holder's members end. Both shapes
 * come from the same walk rather than a second traversal of the arcs.
 */

export interface Group {
  holder: BoardRow;
  /** Everything under the holder, at every depth. */
  members: BoardRow[];
}

export function groupRows(rows: BoardRow[]): Group[] {
  const out: Group[] = [];
  for (const row of rows) {
    if (row.depth === 0) out.push({ holder: row, members: [] });
    else out[out.length - 1]?.members.push(row);
  }
  return out;
}

export interface Shelves {
  /** Storylines, each a band across the wall. */
  shelves: Group[];
  /** Arcs holding nothing. Their head is noise unless a shelf exists above. */
  loose: Group[];
}

export function shelvesOf(rows: BoardRow[]): Shelves {
  const groups = groupRows(rows);
  return {
    shelves: groups.filter((g) => g.members.length > 0),
    loose: groups.filter((g) => g.members.length === 0),
  };
}

/**
 * A holder's direct children, each carrying its OWN descendants.
 *
 * Membership is exclusive, so a member that is itself a storyline renders as
 * one card holding its own members rather than spilling them onto the grid
 * beside it.
 */
export function childEntries(group: Group): Group[] {
  const out: Group[] = [];
  for (const row of group.members) {
    if (row.depth === group.holder.depth + 1) out.push({ holder: row, members: [] });
    else out[out.length - 1]?.members.push(row);
  }
  return out;
}

export interface PlanShelf {
  storyline: Arc;
  /** Its live members, in the storyline's own order. */
  arcs: Arc[];
}

/**
 * The live plan as shelves: every live storyline that holds live arcs is a
 * shelf of them, in its own order; everything live that no live storyline
 * holds sits on its own, after them. An arc held by two storylines is on
 * both — belonging to two sets of work is not duplication. A live storyline
 * holding nothing live is an arc like any other.
 *
 * In play first, queued after, then hand order: the reading the board has
 * always opened on.
 */
export function planShelves(arcs: readonly Arc[]): { shelves: PlanShelf[]; loose: Arc[] } {
  const live = arcs.filter((arc) => isLive(arc.status));
  const byName = new Map(live.map((arc) => [arc.name, arc]));
  const rank = (arc: Arc) => (IN_FLIGHT.includes(arc.status) ? 0 : 1);
  const sorted = [...live].sort(
    (a, b) => rank(a) - rank(b) || a.order - b.order || a.title.localeCompare(b.title),
  );

  const shelves: PlanShelf[] = [];
  const held = new Set<string>();
  for (const storyline of sorted) {
    const members = innerArcNames(storyline)
      .map((name) => byName.get(name))
      .filter((arc): arc is Arc => !!arc && arc.name !== storyline.name);
    if (members.length === 0) continue;
    shelves.push({ storyline, arcs: members });
    for (const member of members) held.add(member.name);
  }

  const shelved = new Set(shelves.map((s) => s.storyline.name));
  const loose = sorted.filter((arc) => !held.has(arc.name) && !shelved.has(arc.name));
  return { shelves, loose };
}
